import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Button from "@material-ui/core/Button";
import readXlsxFile from "read-excel-file";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../theme";
import CommonButton from "../../components/button";
import { ThreeDotsLoading } from "../loading";
import { onBoardEmployeesVerifyAction } from "../../containers/companyDashboard/action";
import { onboardCompanyEmployeesAction } from "../../containers/dashboard/action";
import { hasCap } from "../../lib/permissions";
import ValidationErrorList from "./ValidationErrorList";
import EmployeeFormList from "./employeeFormList";
import ViewSwitcher from "../viewSwitcher";
import { getCompaniesAction } from '../../containers/login/action';
import CommonDropDown from "../dropdown";
import { useSnackbar } from "notistack";
import EmployeeSheetProcessor from "./employeeSheetProcessor";

import {
  employeeImportHeaders,
  isString,
  isNumber,
  isEmail,
  normalizeGender,
} from "./employeeImportValidators";

let fileDownloader;
let fileUploader;

let companyOptions = [];
const headers = employeeImportHeaders;


const useStyles = makeStyles(() => ({
  root: { display: "flex", justifyContent: "center", padding: "30px 0px", width: "100%" },
  container: {
    width: "350px",
    backgroundColor: theme.colors.White,
    boxShadow: "0px 0px 10px #CFD9EA80",
    borderRadius: "15px",
    padding: "50px 60px",
  },
  title: { color: theme.colors.DodgerBlue, fontSize: "24px", fontFamily: theme.fontFamily.MARKPROBOLD, marginBottom: "20px" },
  subText: { color: theme.colors.Violet, fontSize: "18px", fontFamily: theme.fontFamily.MARKPRO, marginBottom: "10px" },
  fileContainer: { backgroundColor: theme.colors.Zumthor, padding: "5px 10px", top: "15px", position: "relative", borderRadius: "3px", wordBreak: "break-word" },
  fileName: { color: theme.colors.DodgerBlue, fontSize: "14px", fontFamily: theme.fontFamily.MARKPROMEDIUM },
  crossIcon: { marginLeft: "8px", marginBottom: "-4px", cursor: "pointer" },
  modal: { display: "flex", alignItems: "center", justifyContent: "center" },
  paper: { position: "absolute", width: 400, backgroundColor: "#fff", border: "2px solid #000", boxShadow: "5px 5px 15px rgba(0, 0, 0, 0.5)", padding: 20 },
  validationModalPaper: {
    position: "absolute",
    width: "min(920px, 92vw)",
    maxHeight: "85vh",
    backgroundColor: "#fff",
    boxShadow: "5px 5px 15px rgba(0, 0, 0, 0.35)",
    borderRadius: "10px",
    padding: "18px 20px",
    overflowY: "auto",
  },
  validationModalTitle: {
    color: theme.colors.Violet,
    fontSize: "18px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginBottom: "8px",
  },
  validationModalSubText: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPRO,
    marginBottom: "14px",
  },
  validationModalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "14px",
  },
  buttonContainer: { display: "flex", justifyContent: "space-between" },
  successText: { color: "#0f9d58", fontFamily: theme.fontFamily.MARKPROMEDIUM, marginTop: "20px" },
  switcherRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  },
  columnStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    width: "100%",
  },
  containerWide: {
    maxWidth: "700px",
    minWidth: "700px",
    boxSizing: "border-box",
    "@media (max-width: 600px)": {
      borderRadius: "10px",
    },
  },

}));

const pushOnce = (errors, message) => {
  if (errors.indexOf(message) < 0) errors.push(message);
};

const validateData = (rows) => {
  const mappedRows = [];
  const errors = [];

  if (!rows || rows.length <= 1) {
    pushOnce(errors, "No data found.");
    return { errors, hasDataRows: false };
  }

  for (let i = 1; i < rows.length; i++) {
    const entry = {};
    for (let j = 0; j < headers.length; j++) entry[headers[j]] = rows[i][j];
    mappedRows.push(entry);
  }

  mappedRows.forEach((element) => {
    if (!element["First Name"] || !element["First Name"]?.trim()) pushOnce(errors, "First Name may not be blank.");
    else if (element["First Name"]?.trim().length > 24) pushOnce(errors, "Ensure First Name has no more than 24 characters.");
    else if (!element["First Name"]?.trim().toString().match(isString)) pushOnce(errors, "First Name should contain only letters and spaces. Numbers and special characters are not allowed.");

    if (!element["Last Name"] || !element["Last Name"]?.trim()) pushOnce(errors, "Last Name may not be blank.");
    else if (element["Last Name"]?.trim().length > 24) pushOnce(errors, "Ensure Last Name has no more than 24 characters.");
    else if (!element["Last Name"]?.trim().toString?.()?.match?.(isString)) pushOnce(errors, "Last Name should contain only letters and spaces. Numbers and special characters are not allowed.");

    if (!element["Gender"] || !element["Gender"]?.trim()) pushOnce(errors, "Gender may not be blank.");
    else {
      const normalizedGender = normalizeGender(element["Gender"]);
      if (normalizedGender !== "M" && normalizedGender !== "F") pushOnce(errors, "Gender should be M/F, Male/Female, or male/female.");
    }

    if (!element["Email"] || !element["Email"]?.trim()) pushOnce(errors, "Email may not be blank.");
    else if (!element["Email"]?.trim().toString?.()?.match?.(isEmail)) pushOnce(errors, "Email should be in proper format.");

    if (!element["Phone number"] || (element["Phone number"] && !Number.toString(element["Phone number"]).trim())) pushOnce(errors, "Phone number may not be blank.");

    if (!element["Title"] || !element["Title"]?.trim()) pushOnce(errors, "Job Title may not be blank.");
    else if (element["Title"]?.trim().length > 24) pushOnce(errors, "Ensure Job Title has no more than 24 characters.");
    else if (!element["Title"]?.trim().toString?.()?.match?.(isString)) pushOnce(errors, "Job Title should contain only letters and spaces. Numbers and special characters are not allowed.");

    if (!element["Full Net Salary"]) pushOnce(errors, "Full Salary may not be blank.");
    else if (!element["Full Net Salary"]?.toString?.()?.match?.(isNumber)) pushOnce(errors, "Full Salary should contain only numbers and decimal point. Letters and special characters (except decimal point) are not allowed.");

    if (!element["Savings"]) pushOnce(errors, "Savings may not be blank.");
    else if (!element["Savings"]?.toString?.()?.match?.(isNumber)) pushOnce(errors, "Savings should be number only.");
    else if (element["Full Net Salary"] && element["Savings"] >= element["Full Net Salary"]) pushOnce(errors, "Savings should be less than Full Salary.");

    if (!element["Account holder name"] || !element["Account holder name"]?.trim()) pushOnce(errors, "Account Holder Name may not be blank.");
    else if (element["Account holder name"]?.trim()?.length > 80) pushOnce(errors, "Ensure Account Holder Name has no more than 80 characters.");
    else if (!element["Account holder name"]?.trim()?.toString()?.match(isString)) pushOnce(errors, "Account holder name should contain only letters and spaces. Numbers and special characters are not allowed.");

    if (!element["Bank Name"] || !element["Bank Name"]?.trim()) pushOnce(errors, "Bank Name may not be blank.");
    else if (element["Bank Name"]?.trim().length > 48) pushOnce(errors, "Ensure Bank Name has no more than 48 characters.");
    else if (!element["Bank Name"]?.trim().toString?.()?.match?.(isString)) pushOnce(errors, "Bank Name should contain only letters and spaces. Numbers and special characters are not allowed.");

    if (!element["Bank Code"] || !element["Bank Code"]?.toString()?.trim()) pushOnce(errors, "Bank Code may not be blank.");
    else if (element["Bank Code"]?.toString()?.trim().length > 6) pushOnce(errors, "Ensure Bank Code has no more than 6 characters.");

    if (!element["Account number"]) pushOnce(errors, "Account Number may not be blank.");
    else if (element["Account number"]?.length > 255) pushOnce(errors, "Ensure Account number has no more than 255 characters.");
    else if (!element["Account number"]?.toString?.()?.match?.(isNumber)) pushOnce(errors, "Account number should be number only.");

    if (element["NIN"] && element["NIN"].toString().length !== 11) pushOnce(errors, "NIN should Be Eleven digits");
    if (element["BVN"] && element["BVN"].toString().length !== 11) pushOnce(errors, "BVN should Be Eleven digits");
  });

  return { errors, hasDataRows: true };
};

function EmployeeValidator() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const companyDashboardReducer = useSelector((state) => state.companyDashboardReducer);
  const dashboardReducer = useSelector((state) => state.dashboardReducer);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isValidFile, setIsValidFile] = useState(false);
  const [apiValidationErrors, setApiValidationErrors] = useState(null);
  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [view, setView] = useState("validator")
  const [selectedCompany, setSelectedCompany] = useState(null);
  const loginReducer = useSelector((state) => state.loginReducer);
  const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
  const [employeesMapped, setEmployeesMapped] = useState([]);
  const snackBar = useSnackbar();

  // Upload tool is gated on employee_upload.view; committing needs employee_upload.create.
  const canView = hasCap(user, "employee_upload.view");
  const canCommit = hasCap(user, "employee_upload.create");

  useEffect(() => {
    if (errors.length) {
      const ele = document.getElementsByClassName("error-container");
      if (ele && ele.length) ele[0].scrollIntoView(false);
    }
  }, [errors]);

  useEffect(() => {
    if (companyDashboardReducer?.onBoardVerifySuccess) {
      setIsValidFile(true);
      setApiValidationErrors(null);
      setIsApiErrorModalOpen(false);
    }
    if (companyDashboardReducer?.onBoardVerifyError) {
      setIsValidFile(false);
      setApiValidationErrors(companyDashboardReducer?.onBoardVerifyError);
      setIsApiErrorModalOpen(true);
    }
  }, [
    companyDashboardReducer?.onBoardVerifySuccess,
    companyDashboardReducer?.onBoardVerifyError,
  ]);

  const onboardCompanyEmployeesMethod = React.useCallback(
    (data) => dispatch(onboardCompanyEmployeesAction(data)), [dispatch]
  );
  const getCompaniesActionMethod = React.useCallback(() => {
    return dispatch(getCompaniesAction())
  }, [dispatch]);

  const mapRowsToEmployees = (rows) => {
    const mappedRows = [];
    for (let i = 1; i < rows.length; i++) {
      const entry = {};
      for (let j = 0; j < headers.length; j++) entry[headers[j]] = rows[i][j];
      mappedRows.push(entry);
    }

    return mappedRows.map((each) => ({
      email: each["Email"] && each["Email"]?.trim(),
      first_name: each["First Name"] && each["First Name"]?.trim(),
      last_name: each["Last Name"] && each["Last Name"]?.trim(),
      gender: each["Gender"] ? normalizeGender(each["Gender"]) : null,
      branch: each["Office Location"] && each["Office Location"]?.trim(),
      employee_id: each["Employee ID"] && each["Employee ID"]?.toString()?.trim(),
      bank: {
        account_holder: each["Account holder name"] && each["Account holder name"]?.trim(),
        name: each["Bank Name"] && each["Bank Name"]?.trim(),
        code: each["Bank Code"] && each["Bank Code"]?.toString()?.trim(),
        number: each["Account number"],
        iban: (each["IBAN(optional)"] && each["IBAN(optional)"].toString().trim()) || null,
      },
      withdrawal_percent: "0",
      profile: {
        position: each["Title"] && each["Title"].trim(),
        phone: each["Phone number"],
        salary: each["Full Net Salary"],
        savings: each["Savings"],
        nin: each["NIN"] && each["NIN"]?.toString(),
        bvn: each["BVN"] && each["BVN"]?.toString(),
      },
    }));
  };

  React.useEffect(() => {
    // if (user?.is_super && !companyOptions.length)
    //   getCompaniesActionMethod();
  }, [getCompaniesActionMethod, user])

  React.useEffect(() => {
    if (loginReducer?.companyOptions?.length) {
      companyOptions = [];
      for (let i = 0; i < loginReducer?.companyOptions.length; i++) {
        const each = loginReducer?.companyOptions[i];
        if (
          companyOptions.findIndex(
            (rec) => rec.value && rec.value === each.id
          ) < 0
        )
          companyOptions.push({ value: each.id, label: each.name });
      }
    }
  }, [loginReducer?.companyOptions]);

  React.useEffect(() => {

    if (dashboardReducer?.onboardCompanyEmployeesError) {
      for (let i = 0; i < dashboardReducer?.onboardCompanyEmployeesError?.length; i++) {
        const eachError = dashboardReducer?.onboardCompanyEmployeesError[i];
        snackBar.enqueueSnackbar(eachError, { variant: "error" });
      }
    }

    if (dashboardReducer?.onboardCompanyEmployeesSuccess) {
      snackBar.enqueueSnackbar("Employee Invited Successfully. ", { variant: "success" });
    }

  }, [dashboardReducer?.onboardCompanyEmployeesSuccess,
  dashboardReducer?.onboardCompanyEmployeesError]);


  // No employee_upload.view (and not super) -> hide the tool entirely.
  if (!canView) return null;

  return (
    <div className={classes.root}>
      <Helmet>
        <title>{"Sanaam - Employee Validator"}</title>
        <meta property="og:title" content={"Sanaam - Employee Validator"} />
      </Helmet>
      <ThreeDotsLoading showLoader={isParsingFile || companyDashboardReducer?.loading} />
      <div className={classes.columnStack}>
        <div className={classes.switcherRow}>
          <ViewSwitcher
            value={view}
            onChange={setView}
            options={[
              { value: "validator", label: "Validate Employee", icon: "/images/upload.png" },
              { value: "manual", label: "Add Employee Manually" },
              { value: "uploadEmployees", label: "Upload Employees File" }
            ]}
          />
        </div>
        {view === "validator" &&
          <div>
            <EmployeeSheetProcessor
              title="Employee File Validator"
              onValidUpload={(data) => {
                dispatch(onBoardEmployeesVerifyAction(data));
              }}
              onFileClose={() => {
                setFile(null);
                setErrors([]);
                setIsValidFile(false);
                setApiValidationErrors(null);
                setIsApiErrorModalOpen(false);
                setEmployeesMapped([]);
              }}
            />
            <Modal
              open={isApiErrorModalOpen}
              onClose={() => setIsApiErrorModalOpen(false)}
              className={classes.modal}
            >
              <div className={classes.validationModalPaper}>
                <div className={classes.validationModalTitle}>API Validation Errors</div>
                <div className={classes.validationModalSubText}>
                  Fix these issues in the uploaded sheet and re-upload to validate again.
                </div>
                <ValidationErrorList
                  errors={Array.isArray(apiValidationErrors) ? apiValidationErrors : [apiValidationErrors]}
                />
                <div className={classes.validationModalFooter}>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: "#3E83FA", color: "#fff" }}
                    onClick={() => setIsApiErrorModalOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </Modal>
          </div>
        }
        {
          view === "manual" &&
          <div className={classes.containerWide}>
            <CommonDropDown
              placeholder={"Select Company"}
              options={companyOptions}
              value={companyOptions.find((v) => v.value === selectedCompany) || null}
              onChange={(data) => {
                if (data?.value) {
                  setSelectedCompany(data.value);
                } else {
                  setSelectedCompany(null);
                }

              }}
              isClearable={true}
              className={classes.companyInput}
            />
            {selectedCompany && <EmployeeFormList canCommit={canCommit} onSubmit={(data) => {
              onboardCompanyEmployeesMethod({ company_id: selectedCompany, dataPayload: data });
            }} />}
          </div>
        }
        {
          view === "uploadEmployees" &&
          (
            <div style={{
              minWidth: "350px"
            }}>
              <CommonDropDown
                placeholder={"Select Company"}
                options={companyOptions}
                value={companyOptions.find((v) => v.value === selectedCompany) || null}
                onChange={(data) => {
                  if (data?.value) {
                    setSelectedCompany(data.value);
                  } else {
                    setSelectedCompany(null);
                  }

                }}
                isClearable={true}
                className={classes.companyInput}
              />
              {selectedCompany &&
                <div>
                  <EmployeeSheetProcessor
                    canCommit={canCommit}
                    loading={
                      companyDashboardReducer?.loading ||
                      dashboardReducer?.loading ||
                      dashboardReducer?.onboardCompanyEmployeesLoading
                    }
                    onValidUpload={(data) => {
                      dispatch(onBoardEmployeesVerifyAction(data));
                    }}
                    onSubmit={(data) => {
                      onboardCompanyEmployeesMethod({ company_id: selectedCompany, dataPayload: data });
                    }}
                    onFileClose={() => {
                      setFile(null);
                      setErrors([]);
                      setIsValidFile(false);
                      setApiValidationErrors(null);
                      setIsApiErrorModalOpen(false);
                      setEmployeesMapped([]);
                    }}
                    hasErrors={(Array.isArray(apiValidationErrors) ? apiValidationErrors : [apiValidationErrors])?.length > 1 ? true : false}
                  />
                  <Modal
                    open={isApiErrorModalOpen}
                    onClose={() => setIsApiErrorModalOpen(false)}
                    className={classes.modal}
                  >
                    <div className={classes.validationModalPaper}>
                      <div className={classes.validationModalTitle}>API Validation Errors</div>
                      <div className={classes.validationModalSubText}>
                        Fix these issues in the uploaded sheet and re-upload to validate again.
                      </div>
                      <ValidationErrorList
                        errors={Array.isArray(apiValidationErrors) ? apiValidationErrors : [apiValidationErrors]}
                      />
                      <div className={classes.validationModalFooter}>
                        <Button
                          variant="contained"
                          style={{ backgroundColor: "#3E83FA", color: "#fff" }}
                          onClick={() => setIsApiErrorModalOpen(false)}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  </Modal>
                </div>
                // </div>
              }
            </div>
          )
        }
      </div>
    </div>
  );
}

export default EmployeeValidator;
